import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';

import { routeTree } from './routeTree.gen';
import { PageError } from './components/Skeleton';
import './styles/index.css';

// Error monitoring — only active in production when a DSN is provided (dev/e2e stay silent).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_APP_VERSION ?? 'dev',
    // Error monitoring only — no performance tracing / session replay (keep bundle light).
    tracesSampleRate: 0,
  });
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  defaultPendingMs: 0,
  defaultPendingMinMs: 300,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <PageError error={error} onHome={() => (window.location.href = '/')} />
      )}
    >
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
